import React, { useContext, useEffect, useState } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { CookiesProvider } from "react-cookie";
import { AppDefs, GuideDefs } from "./components/icons/defs";

import AppLoader from "./components/layouts/app-loader";
import AuthPages from "./components/auth";

import { AppProviders, AppContexts } from "./providers";

import { PermissionsProvider, usePermissionsStore } from "./components/permissions/permissions_store";

/**
 * PermissionsSyncGate
 * Watches the auth context and loads effective permissions whenever
 * the authenticated user changes (login, logout, token refresh).
 * Must be rendered inside both AppProviders (for AuthContext) and
 * PermissionsProvider (for the store).
 */
function PermissionsSyncGate({ children }) {
  const authCtx = useContext(AppContexts.AuthContext);
  const { loadEffectivePermissions, loadRoles, resetPermissions } = usePermissionsStore();
  const [loadedIdentity, setLoadedIdentity] = useState(null);
  const identity = authCtx.user
    ? `${authCtx.user.account_id || authCtx.user.user?.account_id || authCtx.user.details?.account_id}:${authCtx.user.user?.user_id}`
    : null;

  useEffect(() => {
    let active = true;
    resetPermissions();
    setLoadedIdentity(null);
    if (identity) {
      loadEffectivePermissions().then(() => { if (active) setLoadedIdentity(identity); });
      loadRoles();
    }
    return () => { active = false; resetPermissions(); };
  }, [identity, loadEffectivePermissions, loadRoles, resetPermissions]);

  useEffect(() => {
    if (!identity) return undefined;
    const refresh = () => {
      if (document.visibilityState !== "hidden") loadEffectivePermissions();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(timer);
    };
  }, [identity, loadEffectivePermissions]);

  if (identity && loadedIdentity !== identity) return null;

  return children;
}

function App() {
  return (
    <AppProviders>
      <PermissionsProvider>
        <CookiesProvider>
          <BrowserRouter>
            <PermissionsSyncGate>
              <AppDefs />
              <GuideDefs />

              <Switch>
                <Route path="/auth" component={AuthPages} />
                <Route path="/" render={(props) => <AppLoader {...props} />} />
              </Switch>
            </PermissionsSyncGate>
          </BrowserRouter>
        </CookiesProvider>
      </PermissionsProvider>
    </AppProviders>
  );
}

export default App;
